import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Recipe, RecipeFilters, RecipeFormData } from '@/types/recipe'
import { recipeApi } from '@/services/recipe-api'

interface RecipeState {
  recipes: Recipe[]
  filters: RecipeFilters
  loading: boolean
  error: string | null
}

interface RecipeActions {
  loadRecipes: () => Promise<void>
  loadRecipe: (id: string) => Promise<Recipe | null>
  createRecipe: (recipe: RecipeFormData, householdId?: string) => Promise<Recipe | null>
  updateRecipe: (id: string, updates: Partial<RecipeFormData>) => Promise<Recipe | null>
  deleteRecipe: (id: string) => Promise<boolean>
  setRecipes: (recipes: Recipe[]) => void
  setFilters: (filters: RecipeFilters) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  getFilteredRecipes: (userId?: string, householdId?: string, viewMode?: 'personal' | 'household') => Recipe[]
  getSearchableRecipes: () => Recipe[]
  clearFilters: () => void
}

type RecipeStore = RecipeState & RecipeActions

const applyRecipeFilters = (recipes: Recipe[], filters: RecipeFilters) => {
  let filtered = recipes

  if (filters.search) {
    const search = filters.search.toLowerCase()
    filtered = filtered.filter((recipe) =>
      recipe.title.toLowerCase().includes(search) ||
      recipe.description?.toLowerCase().includes(search) ||
      recipe.ingredients.some((ingredient) => ingredient.toLowerCase().includes(search)) ||
      recipe.tags.some((tag) => tag.toLowerCase().includes(search))
    )
  }

  if (filters.difficulty) {
    filtered = filtered.filter((recipe) => recipe.difficulty === filters.difficulty)
  }

  if (filters.meal_type) {
    filtered = filtered.filter((recipe) => recipe.meal_type === filters.meal_type)
  }

  if (filters.tags?.length) {
    filtered = filtered.filter((recipe) => filters.tags!.some((tag) => recipe.tags.includes(tag)))
  }

  if (filters.equipment?.length) {
    filtered = filtered.filter((recipe) =>
      filters.equipment!.some((equipment) => recipe.equipment.includes(equipment))
    )
  }

  if (filters.maxTime) {
    filtered = filtered.filter((recipe) => ((recipe.prep_time || 0) + (recipe.cook_time || 0)) <= filters.maxTime!)
  }

  return [...filtered].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  )
}

export const useRecipeStore = create<RecipeStore>()(
  persist(
    (set, get) => ({
      recipes: [],
      filters: {},
      loading: false,
      error: null,

      loadRecipes: async () => {
        if (get().loading) {
          return
        }

        set({ loading: true, error: null })
        try {
          const recipes = await recipeApi.getRecipes()
          set({ recipes, loading: false })
        } catch (error) {
          set({
            recipes: [],
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to load recipes',
          })
        }
      },

      loadRecipe: async (id: string) => {
        set({ loading: true, error: null })
        try {
          const recipe = await recipeApi.getRecipe(id)
          set((state) => ({
            recipes: state.recipes.some((item) => item.id === recipe.id)
              ? state.recipes.map((item) => (item.id === recipe.id ? recipe : item))
              : [recipe, ...state.recipes],
            loading: false,
          }))
          return recipe
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to load recipe',
          })
          return null
        }
      },

      createRecipe: async (recipeData: RecipeFormData, householdId?: string) => {
        set({ loading: true, error: null })
        try {
          const recipe = await recipeApi.createRecipe({
            ...recipeData,
            household_id: householdId ?? recipeData.household_id,
          })

          set((state) => ({
            recipes: [recipe, ...state.recipes],
            loading: false,
          }))
          return recipe
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to create recipe',
          })
          return null
        }
      },

      updateRecipe: async (id: string, updates: Partial<RecipeFormData>) => {
        set({ loading: true, error: null })
        try {
          const updatedRecipe = await recipeApi.updateRecipe(id, updates)
          set((state) => ({
            recipes: state.recipes.map((recipe) => (recipe.id === id ? updatedRecipe : recipe)),
            loading: false,
          }))
          return updatedRecipe
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to update recipe',
          })
          return null
        }
      },

      deleteRecipe: async (id: string) => {
        set({ loading: true, error: null })
        try {
          await recipeApi.deleteRecipe(id)
          set((state) => ({
            recipes: state.recipes.filter((recipe) => recipe.id !== id),
            loading: false,
          }))
          return true
        } catch (error) {
          set({
            loading: false,
            error: error instanceof Error ? error.message : 'Failed to delete recipe',
          })
          return false
        }
      },

      setRecipes: (recipes) => set({ recipes }),
      setFilters: (filters) => set({ filters }),
      setLoading: (loading) => set({ loading }),
      setError: (error) => set({ error }),
      clearFilters: () => set({ filters: {} }),

      getFilteredRecipes: (userId, householdId, viewMode = 'personal') => {
        const { recipes, filters } = get()

        let filtered = recipes

        if (viewMode === 'household' && householdId) {
          filtered = filtered.filter((recipe) => recipe.household_id === householdId)
        } else {
          filtered = filtered.filter((recipe) => recipe.user_id === userId && !recipe.household_id)
        }

        return applyRecipeFilters(filtered, filters)
      },

      getSearchableRecipes: () => {
        const { recipes, filters } = get()
        return applyRecipeFilters(recipes, filters)
      },
    }),
    {
      name: 'recipe-storage',
      partialize: (state) => ({ recipes: state.recipes }),
    }
  )
)
